import { Client, ExecutionMethod, Functions } from 'appwrite';
import type { TeamInvitationData, TeamMemberData } from '../../../types/n8nResponses';

const FUNCTION_ID = 'team-invite-manager';

interface InviteResult {
  success: boolean;
  data?: { membership_id: string; user_id: string; team_id?: string; status?: 'accepted' | 'pending' };
  error?: string;
}

interface TeamRosterResult {
  success: boolean;
  data?: {
    team_members: TeamMemberData[];
    team_invitations: TeamInvitationData[];
  };
  error?: string;
}

function buildClient() {
  return new Client()
    .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
    .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');
}

async function callFunction(body: Record<string, unknown>): Promise<InviteResult> {
  const functions = new Functions(buildClient());
  const execution = await functions.createExecution(
    FUNCTION_ID,
    JSON.stringify(body),
    false,
    '/',
    ExecutionMethod.POST,
  );
  const result: InviteResult = JSON.parse(execution.responseBody || '{}');
  if (!result.success) {
    throw new Error(result.error || 'team-invite-manager returned failure');
  }
  return result;
}

async function callRosterFunction(body: Record<string, unknown>): Promise<TeamRosterResult> {
  const functions = new Functions(buildClient());
  const execution = await functions.createExecution(
    FUNCTION_ID,
    JSON.stringify(body),
    false,
    '/',
    ExecutionMethod.POST,
  );
  const result: TeamRosterResult = JSON.parse(execution.responseBody || '{}');
  if (!result.success) {
    throw new Error(result.error || 'team-invite-manager returned failure');
  }
  return result;
}

export const teamInviteService = {
  sendInvite(data: {
    invite_email: string;
    invite_name?: string;
    team_id: string;
    admin_user_id: string;
    admin_email: string;
  }): Promise<InviteResult> {
    return callFunction({ action: 'send_invite', parameters: data });
  },

  cancelInvite(data: {
    team_id: string;
    membership_id: string;
  }): Promise<InviteResult> {
    return callFunction({ action: 'cancel_invite', parameters: data });
  },

  removeMember(data: {
    team_id: string;
    membership_id: string;
    remove_user_id?: string;
    remove_user_email?: string;
    admin_user_id: string;
    admin_email: string;
  }): Promise<InviteResult> {
    return callFunction({ action: 'remove_member', parameters: data });
  },

  getTeamRoster(data: {
    team_id?: string;
    admin_user_id?: string;
    admin_email?: string;
  }): Promise<TeamRosterResult> {
    return callRosterFunction({ action: 'get_team_roster', parameters: data });
  },

  /**
   * Team-admin update of a member: access status (active/inactive/blocked —
   * "invited" is owned by the invite flow) and/or label changes
   * (teamadmin ⇄ member). Function validates same-team membership and
   * blocks self-modification.
   */
  updateMember(data: {
    team_id?: string;
    user_id: string;
    status?: 'active' | 'inactive' | 'blocked';
    add_labels?: string[];
    remove_labels?: string[];
    language?: string | null;
    admin_user_id: string;
    admin_email: string;
  }): Promise<InviteResult> {
    return callFunction({ action: 'update_member', parameters: data });
  },
};
